import React, { useState } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, ToastContainer } from 'react-toastify';
import PatientUpdateProfile from './settings_sections/UpdateProfilePage';
import BackNavbar from '../../../components/patient/navigation/BackNavbar';
import { ArrowBackIosNew } from '@mui/icons-material';
import { usePatientAuth } from '../../../components/patient/hooks/usePatientAuth';
import { useNavigate } from 'react-router';
import { APP_URLS } from '../../../../urls';
import { useDispatch } from 'react-redux';

// Additional settings components
const ChangePassword = () => (
  <div style={{ padding: '1rem' }}>
    Change Password Content (to be implemented)
  </div>
);
const NotificationsSettings = () => (
  <div style={{ padding: '1rem' }}>
    Notifications Content (to be implemented)
  </div>
);

const settingsMenu = [
  {
    id: 'updateProfile',
    label: 'Profile Settings',
    Component: PatientUpdateProfile
  },
  {
    id: 'changePassword',
    label: 'Change Password',
    Component: ChangePassword
  },
  {
    id: 'notifications',
    label: 'Notifications',
    Component: NotificationsSettings
  }
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Validate authorization
  const { isAuthenticated } = usePatientAuth(false, true);

  const [activeSection, setActiveSection] = useState(null);
  const listVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  };
  const detailVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 }
  };

  const ActiveComponent = activeSection
    ? settingsMenu.find((section) => section.id === activeSection)?.Component
    : null;
  const activeLabel = activeSection
    ? settingsMenu.find((section) => section.id === activeSection)?.label
    : '';

  const handleBackInSettings = () => {
    // When a section is active, clicking the Navbar back button should go back to the main settings menu.
    setActiveSection(null);
  };

  return (
    <div className={css(styles.outerContainer)}>
      {/* Patient Settings Navbar */}
      {/* <BackNavbar
				title="Settings"
				onClick={() => navigate(APP_URLS.patientDashboard)}
			/> */}
      <div className={css(styles.container)}>
        <AnimatePresence mode='wait'>
          {!activeSection && (
            <motion.div
              key='list'
              variants={listVariants}
              initial='initial'
              animate='animate'
              exit='exit'
              transition={{ duration: 0.3 }}
              className={css(styles.menuContainer)}
            >
              <h2 className={css(styles.pageTitle)}>Settings</h2>
              <ul className={css(styles.menuList)}>
                {settingsMenu.map((section) => (
                  <motion.li
                    key={section.id}
                    className={css(styles.menuItem)}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setActiveSection(section.id)}
                  >
                    {section.label}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          )}
          {activeSection && ActiveComponent && (
            <motion.div
              key='detail'
              variants={detailVariants}
              initial='initial'
              animate='animate'
              exit='exit'
              transition={{ duration: 0.3 }}
              className={css(styles.detailContainer)}
            >
              <div className={css(styles.detailHeader)}>
                <motion.button
                  type='button'
                  onClick={handleBackInSettings}
                  className={css(styles.backButtonDetail)}
                  whileHover={{ scale: 1.1, transition: { duration: 0.2 } }}
                >
                  <ArrowBackIosNew className={css(styles.iconDetail)} />
                </motion.button>
                <h2 className={css(styles.sectionLabel)}>{activeLabel}</h2>
              </div>
              <div className={css(styles.detailContent)}>
                {/* Render the selected settings section */}
                <ActiveComponent toast={toast} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ToastContainer />
    </div>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
    display: 'flex',
    flexDirection: 'column'
  },
  container: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '2rem 1rem',
    boxSizing: 'border-box'
  },
  menuContainer: {
    width: '100%',
    maxWidth: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
    padding: '1.5rem'
  },
  pageTitle: {
    fontSize: '1.75rem',
    color: '#2783F7',
    marginBottom: '1rem',
    textAlign: 'center'
  },
  menuList: {
    listStyle: 'none',
    padding: 0,
    margin: 0
  },
  menuItem: {
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    color: '#4a5568',
    cursor: 'pointer',
    borderBottom: '1px solid #e2e8f0',
    transition: 'background-color 0.3s, transform 0.3s',
    ':hover': {
      backgroundColor: '#f1f5f9'
    }
  },
  detailContainer: {
    width: '100%',
    // maxWidth: '500px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
    // '@media (max-width: 800px)': {
    //   width: '80%'
    // }
  },
  detailHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    backgroundColor: '#f8fafc', // optional
    cursor: 'pointer'
  },
  backButtonDetail: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    marginRight: '1rem'
  },
  iconDetail: {
    fontSize: '1.5rem',
    color: '#2783F7'
  },
  sectionLabel: {
    fontSize: '1.55rem',
    color: '#2783F7',
    margin: 0,
    textAlign: 'left',
    flex: 1
  },
  detailContent: {
    // padding: '1.5rem',
  },
  '@media (max-width: 600px)': {
    menuContainer: {
      padding: '1rem'
    },
    detailContainer: {
      padding: '1rem'
    },
    pageTitle: {
      fontSize: '1.5rem'
    },
    menuItem: {
      fontSize: '0.9rem'
    }
  }
});

export default SettingsPage;
